<template>
  <pre ref="pre"><code class="chat-text language-markdown" v-html="msgFormatted"></code></pre>
</template>
<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { marked, Tokens } from 'marked'
import Prism from 'prismjs'
import 'prismjs/themes/prism-okaidia.css'

@Options({
  props: {
    msg: String
  },
  watch: {
    msg() {
      (this as any).scheduleFormat()
    }
  }
})
export default class Markdown extends Vue {
  msg!: string
  msgFormatted: string = ''
  private formatTimer: number | null = null
  private readonly formatDebounceMs: number = 80

  mounted() {
    this.scheduleFormat()
  }

  // Debounced formatter to avoid heavy re-parsing on each tiny update
  scheduleFormat() {
    if (this.formatTimer != null) {
      window.clearTimeout(this.formatTimer)
    }
    this.formatTimer = window.setTimeout(async () => {
      this.formatTimer = null
      await this.updateMessages()
    }, this.formatDebounceMs)
  }

  async updateMessages() {
    this.msgFormatted = await this.formatMarkdown(this.msg ?? '')
    // Prism.highlightAll()
  }

  async formatMarkdown(text: string): Promise<string> {
    const renderer = new marked.Renderer()
    const linkRenderer = renderer.link
    renderer.link = (link: Tokens.Link) => {
      const html = linkRenderer.call(renderer, link);
      return html.replace(/^<a /, '<a target="_blank" rel="nofollow noreferrer noopener" ');
    }
    const listRenderer = renderer.list
    renderer.list = (list: Tokens.List) => {
      const html = listRenderer.call(renderer, list)
      const style = 'style="margin-left: 40px; padding-left: 0;"'
      return html.replace(/^<ul/, `<ul ${style}`)
        .replace(/^<ol/, `<ol ${style}`)
        .replace(/^<dl/, `<dl ${style}`)
      
    }

    const tableRenderer = renderer.table;
    renderer.table = (table: Tokens.Table) => {
      // Call the original renderer to get the full table HTML
      let html = tableRenderer.call(renderer, table);

      // Add style to the main <table> tag
      const tableStyle = 'style="width: 100%; border-collapse: collapse; margin-top: 16px; margin-bottom: 16px;"';
      html = html.replace(/^<table/, `<table ${tableStyle}`);

      // The default renderer produces <tbody>. We can find it and add styles to its rows.
      const tbodyStartIndex = html.indexOf('<tbody>');
      if (tbodyStartIndex !== -1) {
        const header = html.substring(0, tbodyStartIndex);
        let body = html.substring(tbodyStartIndex);
        
        // Add border-top to each <tr> within the body
        const rowStyle = 'style="border-top: 1px solid #555;"';
        body = body.replace(/<tr/g, `<tr ${rowStyle}`);
        
        html = header + body;
      }

      return html;
    };

    const tablecellRenderer = renderer.tablecell;
    renderer.tablecell = (cell: Tokens.TableCell) => {
      const html = tablecellRenderer.call(renderer, cell);
      const style = 'style="padding: 12px 16px; text-align: left; vertical-align: top;"';
      return html.replace(/^<(th|td)/, `<$1 ${style}`);
    };

    return marked.parse(text, { renderer })
  }
}
</script>
<style scoped lang="scss">
  pre code[class*="language-" ] > ol,
  code[class*="language-"] > ul,
  code[class*="language-"] > ol {
    padding-left: 40px !important;
  }

  code[class*="language-"] > hr:first-child {
    display: none !important;
    opacity: 0;
  }

  pre[class*="language-" ],
  code[class*="language-"] {
    background: rgb(var(--v-theme-theme-code));
    color: rgb(var(--v-theme-on-surface)); // Added for text contrast
    box-sizing: border-box;
    font-family: Avenir, Helvetica, Arial, sans-serif;
    font-size: 18px;
    overflow: auto;
    white-space: normal;
    word-break: break-word;

    ol {
      margin-left: 40px;
    }
    ul {
      margin-left: 40px;
    }
    dl {
      margin-left: 40px;
    }

    p + ol,
    p + ul {
      padding-left: 40px;
    }

    ol {
      padding-left: 40px;
    }

    &:deep(ul, ol, dl) {
      padding-left: 40px;
    }
  }
</style>
